import { cs } from '../../lib/currency';
/**
 * iter335e — Super Admin approval queue for tenant self-service plan
 * changes. Upgrades auto-apply (shown here as "applied"); downgrades
 * land here as "pending" and require approve/reject. Approved downgrades
 * take effect at the tenant's next billing cycle via a nightly job.
 */
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  ArrowLeftRight, Check, X, Loader2, ArrowUpRight, ArrowDownRight, Clock,
} from "lucide-react";
import api from "../../services/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-blue-50 text-blue-700 border-blue-200",
  applied: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const DIR_META = {
  upgrade: { icon: ArrowUpRight, cls: "text-emerald-600", label: "Upgrade" },
  downgrade: { icon: ArrowDownRight, cls: "text-amber-600", label: "Downgrade" },
  lateral: { icon: ArrowLeftRight, cls: "text-slate-500", label: "Lateral" },
  same: { icon: ArrowLeftRight, cls: "text-slate-400", label: "No change" },
};

export default function SuperAdminPlanChangeRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null); // request_id
  const [rejectReason, setRejectReason] = useState("");
  const [approving, setApproving] = useState(null); // request_id
  const [approvalTiming, setApprovalTiming] = useState("next_cycle");
  const [approvalDate, setApprovalDate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/super-admin/plan-change-requests${q}`);
      setRows(data.requests || []);
    } catch (e) {
      toast.error("Failed to load plan change requests");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    if (approvalTiming === "scheduled" && !approvalDate) {
      toast.error("Choose an effective date for the scheduled downgrade.");
      return;
    }
    setBusyId(id);
    try {
      const { data } = await api.post(`/super-admin/plan-change-requests/${id}/approve`, {
        effective_timing: approvalTiming,
        effective_date: approvalTiming === "scheduled" ? approvalDate : null,
      });
      toast.success(
        data.status === "applied"
          ? "Approved and applied immediately"
          : `Approved — effective ${data.effective_date}`
      );
      setApproving(null);
      setApprovalTiming("next_cycle");
      setApprovalDate("");
      // Optimistically reflect the outcome before the next list refresh.
      setRows((prev) => prev.map((r) => r.id === id
        ? { ...r, status: data.status, effective_date: data.effective_date }
        : r));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusyId(null); }
  };

  const reject = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/super-admin/plan-change-requests/${id}/reject`, { reason: rejectReason || null });
      toast.success("Request rejected");
      const reason = rejectReason;
      setRejecting(null); setRejectReason("");
      setRows((prev) => prev.map((r) => r.id === id ? { ...r, status: "rejected", reject_reason: reason } : r));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6" data-testid="plan-change-requests-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ArrowLeftRight className="h-6 w-6 text-blue-600" />
          Plan Change Requests
        </h1>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {["pending", "approved", "applied", "rejected", "all"].map((f) => (
            <button
              key={f}
              data-testid={`plan-req-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-sm rounded-md capitalize transition-colors ${
                filter === f ? "bg-white shadow text-slate-900 font-medium" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center" data-testid="plan-req-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500" data-testid="plan-req-empty">
            No {filter === "all" ? "" : filter} plan change requests.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const dir = DIR_META[r.direction] || DIR_META.same;
            const DirIcon = dir.icon;
            return (
              <Card key={r.id} data-testid={`plan-req-row-${r.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-base">
                    <span className="flex items-center gap-2">
                      <span className="font-semibold">{r.tenant_name || r.tenant_id}</span>
                      <span className={`flex items-center gap-1 text-sm ${dir.cls}`}>
                        <DirIcon className="h-4 w-4" /> {dir.label}
                      </span>
                    </span>
                    <Badge variant="outline" className={STATUS_STYLES[r.status] || ""} data-testid={`plan-req-status-${r.id}`}>
                      {r.status}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-slate-600 space-y-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs uppercase text-slate-400">Plan</p>
                      <p className="font-medium">{r.from_plan_id} → {r.to_plan_id}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-slate-400">Monthly</p>
                      <p className="font-medium tabular-nums">{cs()}{Number(r.current_total || 0).toFixed(2)} → {cs()}{Number(r.new_total || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-slate-400">Requested by</p>
                      <p className="font-medium truncate">{r.requested_by || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> Effective</p>
                      <p className="font-medium">{r.effective_date || "—"}</p>
                    </div>
                  </div>

                  {r.status === "pending" && (
                    rejecting === r.id ? (
                      <div className="space-y-2 pt-2">
                        <Textarea
                          data-testid={`plan-req-reject-reason-${r.id}`}
                          placeholder="Optional reason (shown to the tenant)…"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" variant="destructive" disabled={busyId === r.id}
                            data-testid={`plan-req-confirm-reject-${r.id}`} onClick={() => reject(r.id)}>
                            {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm reject"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setRejecting(null); setRejectReason(""); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : approving === r.id ? (
                      <div className="space-y-3 pt-2 border-t border-slate-100">
                        <p className="text-xs font-medium text-slate-700">When should this downgrade take effect?</p>
                        <label className="flex items-start gap-2 text-sm cursor-pointer">
                          <input type="radio" name={`approval-timing-${r.id}`} value="next_cycle"
                            checked={approvalTiming === "next_cycle"}
                            onChange={(e) => setApprovalTiming(e.target.value)} />
                          <span><b>Next billing cycle</b><br /><span className="text-xs text-slate-500">Default; access remains unchanged until renewal.</span></span>
                        </label>
                        <label className="flex items-start gap-2 text-sm cursor-pointer">
                          <input type="radio" name={`approval-timing-${r.id}`} value="immediate"
                            checked={approvalTiming === "immediate"}
                            onChange={(e) => setApprovalTiming(e.target.value)} />
                          <span><b>Immediately</b><br /><span className="text-xs text-slate-500">Removes the downgraded access now.</span></span>
                        </label>
                        <label className="flex items-start gap-2 text-sm cursor-pointer">
                          <input type="radio" name={`approval-timing-${r.id}`} value="scheduled"
                            checked={approvalTiming === "scheduled"}
                            onChange={(e) => setApprovalTiming(e.target.value)} />
                          <span className="flex flex-col gap-1"><b>On a selected date</b>
                            <input type="date" value={approvalDate} min={new Date().toISOString().slice(0, 10)}
                              disabled={approvalTiming !== "scheduled"}
                              onChange={(e) => setApprovalDate(e.target.value)}
                              data-testid={`plan-req-effective-date-${r.id}`}
                              className="w-44 border border-slate-300 rounded px-2 py-1 text-sm" />
                          </span>
                        </label>
                        <div className="flex gap-2">
                          <Button size="sm" disabled={busyId === r.id} className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`plan-req-confirm-approve-${r.id}`} onClick={() => approve(r.id)}>
                            {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Confirm approval</>}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setApproving(null); setApprovalTiming("next_cycle"); setApprovalDate(""); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" disabled={busyId === r.id} className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`plan-req-approve-${r.id}`} onClick={() => setApproving(r.id)}>
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`plan-req-reject-${r.id}`} onClick={() => setRejecting(r.id)}>
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    )
                  )}

                  {r.status === "applied" && r.charge && r.charge.ok === false && (
                    <p className="text-xs text-red-600 pt-1" data-testid={`plan-req-charge-warn-${r.id}`}>
                      Proration not charged: {r.charge.error}
                    </p>
                  )}
                  {r.status === "rejected" && r.reject_reason && (
                    <p className="text-xs text-slate-500 pt-1">Reason: {r.reject_reason}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
